import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { Room, ROOM_TYPE_LABELS } from '@/types/profile'

interface RoomQrDialogProps {
  room: Room
  open: boolean
  onClose: () => void
}

export const RoomQrDialog = ({ room, open, onClose }: RoomQrDialogProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const roomUrl = `${window.location.origin}/room/${room.id}/qr-entry`
  const isExpired = room.expiresAt != null && new Date(room.expiresAt) < new Date()

  useEffect(() => {
    // 期限切れのルームはQRを生成しない。
    // 配布しても入室できないため、表示・ダウンロードともに行わせない。
    if (!open || isExpired) return
    setLoading(true)
    setError(false)

    // canvasが描画されるまで少し待つ
    const timer = setTimeout(async () => {
      if (!canvasRef.current) return
      try {
        await QRCode.toCanvas(canvasRef.current, roomUrl, {
          width: 240,
          margin: 2,
          color: { dark: '#1e1b4b', light: '#ffffff' },
        })
        setLoading(false)
      } catch {
        setError(true)
        setLoading(false)
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [open, roomUrl, isExpired])

  const formatDateTime = (date?: Date) =>
    date
      ? new Intl.DateTimeFormat('ja-JP', {
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }).format(date)
      : '未設定'

  /**
   * QRコード画像にルーム情報を添えて1枚のPNGにする。
   * 配布された紙面だけで、どのイベントのQRか判別できるようにする。
   * ※パスワードは会場で口頭開示する運用のため、意図的に含めない。
   */
  const handleDownload = async () => {
    // 期限切れのQRは配布させない（ボタン無効化に加えた二重の防御）
    if (isExpired) return
    try {
      const qrDataUrl = await QRCode.toDataURL(roomUrl, {
        width: 512,
        margin: 1,
        color: { dark: '#1e1b4b', light: '#ffffff' },
      })

      const qrImage = new Image()
      await new Promise<void>((resolve, reject) => {
        qrImage.onload = () => resolve()
        qrImage.onerror = () => reject(new Error('QR image load failed'))
        qrImage.src = qrDataUrl
      })

      const W = 800
      const PAD = 48
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // 折り返しを考慮して必要な高さを先に測る
      const wrap = (text: string, maxWidth: number, font: string): string[] => {
        ctx.font = font
        const lines: string[] = []
        for (const paragraph of text.split(String.fromCharCode(10))) {
          let line = ''
          for (const ch of paragraph) {
            if (ctx.measureText(line + ch).width > maxWidth && line) {
              lines.push(line)
              line = ch
            } else {
              line += ch
            }
          }
          lines.push(line)
        }
        return lines
      }

      const bodyWidth = W - PAD * 2
      const titleFont = 'bold 34px sans-serif'
      const labelFont = '20px sans-serif'
      const valueFont = 'bold 24px sans-serif'
      const descFont = '22px sans-serif'

      const titleLines = wrap(room.name, bodyWidth, titleFont)
      const descLines = room.description ? wrap(room.description, bodyWidth, descFont) : []

      const qrSize = 420
      let h = PAD
      h += titleLines.length * 44 + 12
      h += 40 // カテゴリ
      if (descLines.length) h += descLines.length * 32 + 16
      h += 24 + qrSize + 28 // QR
      h += 2 * 64 // 開催日時 / 有効期限
      h += PAD

      canvas.width = W
      canvas.height = h

      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, W, h)

      let y = PAD

      // ルーム名
      ctx.fillStyle = '#1e1b4b'
      ctx.font = titleFont
      ctx.textAlign = 'center'
      for (const line of titleLines) {
        y += 34
        ctx.fillText(line, W / 2, y)
        y += 10
      }
      y += 12

      // カテゴリ
      ctx.font = labelFont
      ctx.fillStyle = '#6366f1'
      ctx.fillText(ROOM_TYPE_LABELS[room.type], W / 2, y + 22)
      y += 40

      // ルーム詳細
      if (descLines.length) {
        ctx.font = descFont
        ctx.fillStyle = '#475569'
        for (const line of descLines) {
          y += 26
          ctx.fillText(line, W / 2, y)
          y += 6
        }
        y += 16
      }

      // QRコード
      y += 24
      ctx.drawImage(qrImage, (W - qrSize) / 2, y, qrSize, qrSize)
      y += qrSize + 28

      // 開催日時・有効期限
      ctx.textAlign = 'left'
      const rows: [string, string][] = [
        ['開催日時', formatDateTime(room.eventDate)],
        ['有効期限', formatDateTime(room.expiresAt)],
      ]
      for (const [label, value] of rows) {
        ctx.font = labelFont
        ctx.fillStyle = '#64748b'
        ctx.fillText(label, PAD, y + 22)
        ctx.font = valueFont
        ctx.fillStyle = '#0f172a'
        ctx.fillText(value, PAD + 130, y + 22)
        y += 64
      }

      const a = document.createElement('a')
      a.href = canvas.toDataURL('image/png')
      a.download = `QR_${room.name.replace(/\s+/g, '_')}.png`
      a.click()
    } catch {
      // do nothing
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
    >
      <DialogTitle sx={{ pr: 5, fontWeight: 700 }}>
        入室用QRコード
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 10, right: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {/* 期限切れのQRを配布してしまわないよう、この場で警告する */}
        {isExpired && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            このルームは有効期限切れです。このQRコードからは入室できません。
          </Alert>
        )}

        {/* ダウンロード画像と同じ内容を画面でも確認できるようにする */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography fontWeight={700}>{room.name}</Typography>
          <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
            {ROOM_TYPE_LABELS[room.type]}
          </Typography>
          {room.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {room.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            開催日時: {formatDateTime(room.eventDate)}
          </Typography>
          <Typography variant="caption" color={isExpired ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
            有効期限: {formatDateTime(room.expiresAt)}
          </Typography>
        </Box>

        {/* QR表示エリア */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            minHeight: 264,
          }}
        >
          {isExpired ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              有効期限切れのため、QRコードは表示できません
            </Typography>
          ) : (
            <>
              {loading && <CircularProgress size={40} />}
              {error && (
                <Typography variant="body2" color="error">
                  QRコードの生成に失敗しました
                </Typography>
              )}
              <canvas
                ref={canvasRef}
                style={{ display: loading || error ? 'none' : 'block', borderRadius: 4 }}
              />
            </>
          )}
        </Box>

        {!isExpired && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center', mt: 1.5, wordBreak: 'break-all' }}
          >
            {roomUrl}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={loading || error || isExpired}
          sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
        >
          QRコードをダウンロード
        </Button>
        <Button
          variant="text"
          fullWidth
          color="inherit"
          onClick={onClose}
          sx={{ borderRadius: 2, color: 'text.secondary' }}
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  )
}
